import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import DataPicker from "./DataPicker";
import { useFlussFieldPath, useFlussInlineIssues } from "./node-properties/FlussNodePanelField";
import { FieldMapping, ScopeOption } from "./fluss-builder-types";

export interface FieldMapperProps {
    value: FieldMapping[];
    onChange: (value: FieldMapping[]) => void;
    targetFields: ScopeOption[];
    // `record.` of this mapping when it is not the process anchor table (work record, signal table).
    recordTable?: string;
    disabled?: boolean;
    // PRIM-015: required destination fields only make sense to force when the mapping creates a
    // record — an update mapping targets a record that already satisfied them. Defaults to true
    // so every existing call site (creation flows) keeps its current behavior unchanged; pass
    // false only where the caller actually knows it's editing an update mapping.
    enforceRequired?: boolean;
    templateText?: boolean;
}

export const FieldMapper = ({
    value,
    onChange,
    targetFields,
    recordTable,
    disabled = false,
    enforceRequired = true,
    templateText = false,
}: FieldMapperProps) => {
    const { t } = useTranslation();
    const fieldPath = useFlussFieldPath();
    const inlineIssues = useFlussInlineIssues();
    const rows = useMemo(() => {
        const byTarget = new Map(value.map((mapping) => [mapping.target, mapping]));
        const mandatory = enforceRequired
            ? targetFields
                .filter((field) => field.required)
                .map((field) => byTarget.get(field.name) || { target: field.name, source: undefined })
            : [];
        const remaining = value.filter((mapping) => !mandatory.some((item) => item.target === mapping.target));
        return [...mandatory, ...remaining];
    }, [targetFields, value, enforceRequired]);

    const emitRows = (nextRows: FieldMapping[]) => {
        const mandatoryTargets = enforceRequired
            ? new Set(targetFields.filter((field) => field.required).map((field) => field.name))
            : new Set<string>();
        const normalized = [
            ...(enforceRequired
                ? targetFields
                    .filter((field) => field.required && !nextRows.some((row) => row.target === field.name))
                    .map((field) => ({ target: field.name, source: undefined }))
                : []),
            ...nextRows,
        ].filter((row, index, all) =>
            row.target === "" || all.findIndex((candidate) => candidate.target === row.target) === index
        );
        if (mandatoryTargets.size || normalized.length !== value.length || normalized.some((row, index) => row !== value[index])) {
            onChange(normalized);
        }
    };

    const updateRow = (index: number, patch: Partial<FieldMapping>) => {
        emitRows(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    };

    const removeRow = (index: number) => {
        emitRows(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    return (
        <Stack spacing={1} data-testid="field-mapper" className="fluss-mapper">
            <Box
                sx={{ display: "grid", gap: 1 }}
            >

                {rows.length === 0 && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ gridColumn: "1 / -1", py: 1 }}
                    >
                        {t("fluss.builder.field_mapper.empty", {
                            defaultValue: "Add a field to define the mapping.",
                        })}
                    </Typography>
                )}

                {rows.map((mapping, index) => {
                    const target = targetFields.find((field) => field.name === mapping.target);
                    const mandatory = enforceRequired && Boolean(target?.required);
                    const fieldIssue = inlineIssues.filter((issue) =>
                        issue.field === `${fieldPath}.${mapping.target}` || issue.field === `config.${fieldPath}.${mapping.target}`);
                    const message = fieldIssue[0]?.message || "";
                    return (
                        <Box
                            key={`${mapping.target || "new"}:${index}`}
                            className="fluss-mapper__row"
                            data-testid="field-mapper-row"
                        >
                            <Box className="fluss-mapper__destination">
                                <Typography variant="caption" color="text.secondary">
                                    {t("fluss.builder.field_mapper.destination", { defaultValue: "Destination field" })}
                                </Typography>
                            <Select
                                size="small"
                                value={mapping.target}
                                MenuProps={{ MenuListProps: { onKeyDownCapture: (event) => {
                                    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                                        event.stopPropagation();
                                    }
                                } } }}
                                disabled={disabled}
                                displayEmpty
                                inputProps={{
                                    "aria-label": String(t("fluss.builder.field_mapper.destination", {
                                        defaultValue: "Destination field",
                                    })),
                                }}
                                onChange={(event) => updateRow(index, {
                                    target: event.target.value,
                                    source: undefined,
                                })}
                            >
                                <MenuItem value="" disabled>
                                    {t("fluss.builder.field_mapper.select_field", {
                                        defaultValue: "Select a field",
                                    })}
                                </MenuItem>
                                {targetFields.map((field) => (
                                    <MenuItem
                                        key={field.name}
                                        value={field.name}
                                        disabled={rows.some((row, rowIndex) =>
                                            rowIndex !== index && row.target === field.name
                                        )}
                                    >
                                        {translateMetadataLabel(t, field) || field.label}{field.required ? " *" : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                            </Box>
                            <Typography
                                className="fluss-mapper__arrow"
                                aria-hidden="true"
                                color="text.secondary"
                                textAlign="center"
                                fontWeight={700}
                            >
                                ←
                            </Typography>
                            <Box className="fluss-mapper__source">
                                <Typography variant="caption" color="text.secondary">
                                    {t("fluss.builder.field_mapper.source", { defaultValue: "Source" })}
                                </Typography>
                            <DataPicker
                                value={mapping.source}
                                fieldPath={`${fieldPath}.${mapping.target}`}
                                onChange={(source) => updateRow(index, { source })}
                                recordTable={recordTable}
                                expectedType={target?.type}
                                referencedTable={target?.type === "REFERENCE" ? target.referencedTableName : undefined}
                                destinationField={target}
                                literalTemplate={templateText && ["TEXT", "RICH_HTML"].includes(target?.type || "")}
                                disabled={disabled}
                                error={Boolean(message)}
                                helperText={message}
                                ariaLabel={String(t("fluss.builder.field_mapper.source_for", {
                                    defaultValue: "Source for {{field}}",
                                    field: (target ? translateMetadataLabel(t, target) : "") || target?.label || "",
                                }))}
                            />
                            </Box>
                            <Tooltip
                                title={mandatory
                                    ? t("fluss.builder.field_mapper.required_cannot_remove", {
                                        defaultValue: "Required fields cannot be removed.",
                                    })
                                    : t("fluss.builder.field_mapper.remove", { defaultValue: "Remove field" })}
                            >
                                <span className="fluss-mapper__remove">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={disabled || mandatory}
                                        aria-label={String(t("fluss.builder.field_mapper.remove", {
                                            defaultValue: "Remove field",
                                        }))}
                                        onClick={() => removeRow(index)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>
            <Box>
                <Button
                    size="small"
                    disabled={disabled}
                    startIcon={<AddIcon />}
                    onClick={() => emitRows([...rows, { target: "", source: undefined }])}
                >
                    {t("fluss.builder.field_mapper.add", { defaultValue: "Field" })}
                </Button>
            </Box>
        </Stack>
    );
};

export default FieldMapper;
